import { type StateCreator } from "zustand";
import { type StateStorage } from "zustand/middleware";

type PersistMutators = [["zustand/persist", unknown]];
type SetStateArgument<T> = T | Partial<T> | ((state: T) => T | Partial<T>);

/**
 * Stops a persisted store from saving its defaults over the visitor's saved
 * state.
 *
 * Our persisted stores use `skipHydration` and load the saved state from an
 * effect in InitialDataLoader, but child components run their effects first,
 * and zustand's persist saves the WHOLE state on every set(). One early set()
 * (a popup flag) therefore replaced the saved state with the defaults: the
 * `?a=` referral came back as the brand's own code after a reload or in a new
 * tab, and the coupon code was lost the same way.
 *
 * Until the saved state has loaded, the guard keeps writes out of storage and
 * remembers which keys were set. When it loads, those keys keep the values
 * set on this page and everything else comes from storage.
 *
 * Usage: `persist(guard.track((set, get) => ({...})), { storage:
 * createJSONStorage(() => guard.storage(encryptedStorage)), merge:
 * guard.merge })`.
 */
interface HydrationGuard<T> {
  track: (
    creator: StateCreator<T, PersistMutators, []>,
  ) => StateCreator<T, PersistMutators, []>;
  storage: (inner: StateStorage) => StateStorage;
  merge: (persistedState: unknown, currentState: T) => T;
}

export const createHydrationGuard = <T extends object>(): HydrationGuard<T> => {
  let isLoaded = false;
  const keysSetEarly = new Set<keyof T>();

  const track =
    (
      creator: StateCreator<T, PersistMutators, []>,
    ): StateCreator<T, PersistMutators, []> =>
    (set, get, api) => {
      const setState = set as (
        partial: SetStateArgument<T>,
        replace?: boolean,
      ) => void;
      const trackedSet = (
        partial: SetStateArgument<T>,
        replace?: boolean,
      ): void => {
        if (isLoaded) {
          setState(partial, replace);
          return;
        }
        const next = typeof partial === "function" ? partial(get()) : partial;
        (Object.keys(next) as Array<keyof T>).forEach(key => {
          keysSetEarly.add(key);
        });
        setState(next, replace);
      };
      return creator(trackedSet as typeof set, get, api);
    };

  const storage = (inner: StateStorage): StateStorage => ({
    // Must stay synchronous: an async getItem would make rehydrate() async,
    // and InitialDataLoader reads the store right after calling it.
    // eslint-disable-next-line @typescript-eslint/promise-function-async
    getItem: name => {
      try {
        return inner.getItem(name);
      } catch (error) {
        // Nothing will be loaded, so later writes must not be held back.
        isLoaded = true;
        throw error;
      }
    },
    setItem: async (name, value) => {
      if (isLoaded) {
        await inner.setItem(name, value);
      }
    },
    removeItem: async name => {
      await inner.removeItem(name);
    },
  });

  const merge = (persistedState: unknown, currentState: T): T => {
    isLoaded = true;
    const merged: T = {
      ...currentState,
      ...((persistedState ?? {}) as Partial<T>),
    };
    keysSetEarly.forEach(key => {
      merged[key] = currentState[key];
    });
    keysSetEarly.clear();
    return merged;
  };

  return { track, storage, merge };
};
